// components/BookReader.tsx — reader view for a story's chapters.
//
// Loads `chaptersQuery(storyId)` on mount. Renders a two-pane layout:
//   - Sidebar: ordered chapter list, current-chapter highlighted, click
//     to navigate.
//   - Main: hand-rendered markdown for the active chapter using the
//     scenario's theme tokens for typography.
//   - Footer: "Continue playing" (calls onContinuePlaying) + "Back to
//     bookshelf" (calls onBack). Share button is a disabled placeholder.
//
// Reading-position memory: scroll position per (storyId, chapterId)
// persisted to localStorage under "ember.reading.<storyId>.<chapterId>".
// Restored on chapter open; written on scroll (debounced).
//
// No markdown library — the chapter prose is structured enough (paragraphs
// separated by blank lines; rare H2 sections) that a hand-roll is cleaner
// than adding a dependency. Inline emphasis (asterisks, underscores) is
// preserved verbatim — the model is told to emit prose markdown but
// rarely uses inline marks in chapter prose.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  chaptersQuery,
  forkOwnStory,
  type ChapterDto,
} from "../services/booksApi";
import { SCENARIO_PRESENTATIONS } from "../scenarios/presentation";
import ShareModal from "./ShareModal";

export interface BookReaderProps {
  storyId: string;
  scenarioId: string;
  storyName: string;
  authorName: string;
  /** Optional: open at this chapter (1-based). Falls back to chapter 1. */
  initialChapterNumber?: number;
  onBack: () => void;
  onContinuePlaying: () => void;
  /**
   * When true (author viewing their OWN story), each chapter shows a
   * "Continue the story from here" action that forks the story PRIVATELY
   * (no publishing) at that chapter and hands the new story id up via
   * `onForkToStory`. Off by default — public/foreign readers never see it.
   */
  showBranchActions?: boolean;
  /**
   * Called after a successful private fork with the new story's id. The
   * app uses its existing activate-story path to switch the player into
   * the new continuation. Required for the branch action to function.
   */
  onForkToStory?: (newStoryId: string) => void;
  /** Optional toast surface for fork errors. */
  onError?: (message: string) => void;
}

const SCROLL_KEY_PREFIX = "ember.reading";

function readScrollFromStorage(storyId: string, chapterId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(
      `${SCROLL_KEY_PREFIX}.${storyId}.${chapterId}`,
    );
    if (raw === null) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeScrollToStorage(
  storyId: string,
  chapterId: string,
  scrollTop: number,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${SCROLL_KEY_PREFIX}.${storyId}.${chapterId}`,
      String(Math.max(0, Math.floor(scrollTop))),
    );
  } catch {
    // ignore
  }
}

/**
 * Render the chapter body. We split on blank lines for paragraph
 * boundaries and treat `# `, `## `, `### ` prefixes as heading levels.
 * Inline asterisks are passed through unchanged — readers see them, but
 * the model rarely emits them in chapter prose so it doesn't visually
 * distract.
 */
function renderMarkdownBody(body: string, fontFamily: string): JSX.Element[] {
  const blocks = body.split(/\n\s*\n/);
  return blocks.map((raw, i) => {
    const trimmed = raw.trim();
    if (!trimmed) return <div key={i} />;
    if (/^###\s+/.test(trimmed)) {
      return (
        <h4
          key={i}
          style={{
            margin: "1.4em 0 0.4em",
            fontFamily,
            fontSize: "1.1em",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {trimmed.replace(/^###\s+/, "")}
        </h4>
      );
    }
    if (/^##\s+/.test(trimmed)) {
      return (
        <h3
          key={i}
          style={{
            margin: "1.6em 0 0.5em",
            fontFamily,
            fontSize: "1.25em",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {trimmed.replace(/^##\s+/, "")}
        </h3>
      );
    }
    if (/^#\s+/.test(trimmed)) {
      return (
        <h2
          key={i}
          style={{
            margin: "1.8em 0 0.6em",
            fontFamily,
            fontSize: "1.4em",
            fontWeight: 500,
          }}
        >
          {trimmed.replace(/^#\s+/, "")}
        </h2>
      );
    }
    // Regular paragraph. Preserve internal line breaks (rare; the prose
    // generator usually emits a single line per paragraph).
    return (
      <p
        key={i}
        style={{
          margin: "0 0 1.1em",
          lineHeight: 1.7,
          fontFamily,
          fontSize: "1.05em",
        }}
      >
        {trimmed.split(/\n/).map((line, j, arr) => (
          <span key={j}>
            {line}
            {j < arr.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}

export default function BookReader({
  storyId,
  scenarioId,
  storyName,
  authorName,
  initialChapterNumber,
  onBack,
  onContinuePlaying,
  showBranchActions = false,
  onForkToStory,
  onError,
}: BookReaderProps): JSX.Element {
  const { t } = useTranslation(["bookshelf", "scenarios"]);
  const [chapters, setChapters] = useState<ChapterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Per-chapter fork: the chapterNumber currently being forked (null = idle),
  // so we can show a busy state on just the active button.
  const [forkingChapter, setForkingChapter] = useState<number | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);

  // Theme tokens for typography
  const presentation = SCENARIO_PRESENTATIONS[scenarioId];
  const theme = presentation?.theme;
  const fontFamily =
    theme?.fontFamily ??
    "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
  const accent = theme?.accent ?? "#d4a574";
  const accentMuted = theme?.accentMuted ?? "rgba(212,165,116,0.45)";
  const ink = theme?.ink ?? "#e8d9b8";
  const inkMuted = theme?.inkMuted ?? "#8a7355";
  const paper = theme?.paper ?? "rgba(20,14,10,0.55)";
  const paperEdge = theme?.paperEdge ?? "rgba(212,165,116,0.18)";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await chaptersQuery(storyId);
        if (cancelled) return;
        setChapters(res.chapters);
        const initial =
          res.chapters.find(
            (c) => c.chapterNumber === (initialChapterNumber ?? 1),
          ) ?? res.chapters[0];
        setActiveChapterId(initial?.id ?? null);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storyId, initialChapterNumber]);

  const activeChapter = useMemo<ChapterDto | null>(
    () => chapters.find((c) => c.id === activeChapterId) ?? null,
    [chapters, activeChapterId],
  );

  // Restore scroll position when chapter changes.
  useEffect(() => {
    const main = mainRef.current;
    if (!main || !activeChapter) return;
    const saved = readScrollFromStorage(storyId, activeChapter.id);
    // Use a microtask so the new content lays out first.
    queueMicrotask(() => {
      if (mainRef.current) {
        mainRef.current.scrollTop = saved;
      }
    });
  }, [activeChapter, storyId]);

  // Persist scroll on user-initiated scroll. Debounced via a ref so we
  // don't write on every pixel.
  const scrollWriteTimer = useRef<number | null>(null);
  const handleMainScroll = useCallback(() => {
    if (!activeChapter) return;
    if (scrollWriteTimer.current !== null) {
      window.clearTimeout(scrollWriteTimer.current);
    }
    const top = mainRef.current?.scrollTop ?? 0;
    scrollWriteTimer.current = window.setTimeout(() => {
      writeScrollToStorage(storyId, activeChapter.id, top);
    }, 250);
  }, [activeChapter, storyId]);

  // ── Private fork ("Continue the story from here") ─────────────────────
  // Forks the author's OWN story at the given chapter into a NEW private
  // continuation, then hands the new story id up so the app switches into
  // it. Never publishes. Disabled while a fork is in flight.
  const handleForkFromChapter = useCallback(
    async (chapterNumber: number) => {
      if (!onForkToStory || forkingChapter !== null) return;
      setForkingChapter(chapterNumber);
      try {
        const { newStoryId } = await forkOwnStory(storyId, chapterNumber);
        onForkToStory(newStoryId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        onError?.(
          t("bookshelf:bookReader.continueHereFailed", {
            message: msg,
            defaultValue: `Couldn't branch the story: ${msg}`,
          }),
        );
        setForkingChapter(null);
      }
      // On success we leave forkingChapter set — the app is about to swap
      // this whole reader out for the new story's play surface.
    },
    [onForkToStory, forkingChapter, storyId, onError, t],
  );

  // ── Styles ──────────────────────────────────────────────────────────
  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "#0a0706",
    color: ink,
    fontFamily,
    display: "flex",
    flexDirection: "column",
    zIndex: 80,
  };
  const header: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "16px 24px",
    borderBottom: `1px solid ${paperEdge}`,
    background: paper,
  };
  const titleStyle: CSSProperties = {
    margin: 0,
    fontFamily,
    fontSize: "1.3rem",
    fontWeight: 500,
    color: accent,
    flex: 1,
  };
  const subtitleStyle: CSSProperties = {
    margin: 0,
    fontSize: "0.78rem",
    color: inkMuted,
    fontStyle: "italic",
  };
  const backBtn: CSSProperties = {
    background: "transparent",
    color: inkMuted,
    border: `1px solid ${paperEdge}`,
    padding: "6px 14px",
    fontSize: "0.8rem",
    fontFamily: "'JetBrains Mono', monospace",
    cursor: "pointer",
    borderRadius: 2,
  };
  const main: CSSProperties = {
    display: "flex",
    flex: 1,
    minHeight: 0,
  };
  const sidebar: CSSProperties = {
    width: 260,
    flexShrink: 0,
    borderRight: `1px solid ${paperEdge}`,
    background: paper,
    overflowY: "auto",
    padding: "16px 0",
  };
  const sidebarTitle: CSSProperties = {
    padding: "0 20px 12px",
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
  };
  const chapterListItem = (active: boolean): CSSProperties => ({
    padding: "10px 20px",
    cursor: "pointer",
    color: active ? accent : ink,
    background: active ? `${accentMuted}` : "transparent",
    borderLeft: active ? `3px solid ${accent}` : "3px solid transparent",
    fontSize: "0.92rem",
    transition: "background 0.18s, color 0.18s",
  });
  const chapterListLabel: CSSProperties = {
    fontSize: "0.65rem",
    fontFamily: "'JetBrains Mono', monospace",
    color: inkMuted,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 4,
  };
  const body: CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: "48px 64px",
    maxWidth: 760,
    margin: "0 auto",
    width: "100%",
    color: ink,
  };
  const footer: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderTop: `1px solid ${paperEdge}`,
    background: paper,
  };
  const footerLeftBtn: CSSProperties = {
    background: "transparent",
    color: accent,
    border: `1px solid ${accentMuted}`,
    padding: "8px 18px",
    fontSize: "0.85rem",
    fontFamily,
    cursor: "pointer",
    borderRadius: 2,
  };
  const shareBtn: CSSProperties = {
    background: "transparent",
    color: accent,
    border: `1px solid ${accentMuted}`,
    padding: "8px 18px",
    fontSize: "0.85rem",
    fontFamily,
    cursor: "pointer",
    borderRadius: 2,
  };
  const empty: CSSProperties = {
    padding: 64,
    color: inkMuted,
    fontStyle: "italic",
    textAlign: "center",
  };
  const branchWrap: CSSProperties = {
    marginTop: 40,
    paddingTop: 24,
    borderTop: `1px solid ${paperEdge}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
  };
  const branchHint: CSSProperties = {
    margin: 0,
    fontSize: "0.8rem",
    color: inkMuted,
    fontStyle: "italic",
  };
  const branchBtn = (busy: boolean): CSSProperties => ({
    background: "transparent",
    color: accent,
    border: `1px solid ${accent}`,
    padding: "9px 20px",
    fontSize: "0.88rem",
    fontFamily,
    cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.6 : 1,
    borderRadius: 2,
  });

  return (
    <div style={shell}>
      <header style={header}>
        <div>
          <h1 style={titleStyle}>{storyName || t("bookshelf:bookReader.untitled")}</h1>
          <p style={subtitleStyle}>
            {t("bookshelf:bookReader.byline", {
              scenario: t(`scenarios:${scenarioId}.pillLabel`, {
                defaultValue: presentation?.pillLabel ?? scenarioId,
              }),
              author: authorName,
            })}
          </p>
        </div>
        <button
          type="button"
          style={backBtn}
          onClick={onBack}
          aria-label={t("bookshelf:bookReader.backAria")}
        >
          {t("bookshelf:bookReader.backToShelf")}
        </button>
      </header>

      <div style={main}>
        <aside style={sidebar}>
          <div style={sidebarTitle}>{t("bookshelf:bookReader.chapters")}</div>
          {loading && (
            <div style={{ padding: "0 20px", color: inkMuted, fontStyle: "italic" }}>
              {t("bookshelf:bookReader.loading")}
            </div>
          )}
          {!loading && chapters.length === 0 && !error && (
            <div style={{ padding: "0 20px", color: inkMuted, fontStyle: "italic" }}>
              {t("bookshelf:bookReader.noChapters")}
            </div>
          )}
          {error && (
            <div style={{ padding: "0 20px", color: "#c47a5a" }}>
              {t("bookshelf:bookReader.couldntLoad", { message: error })}
            </div>
          )}
          {chapters.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => setActiveChapterId(c.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveChapterId(c.id);
                }
              }}
              style={chapterListItem(c.id === activeChapterId)}
            >
              <div style={chapterListLabel}>
                {t("bookshelf:bookReader.chapterLabel", { number: c.chapterNumber })}
              </div>
              <div>{c.title}</div>
            </div>
          ))}
        </aside>

        <main ref={mainRef} style={body} onScroll={handleMainScroll}>
          {activeChapter ? (
            <>
              <div
                style={{
                  fontSize: "0.7rem",
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: inkMuted,
                  marginBottom: 8,
                }}
              >
                {t("bookshelf:bookReader.chapterLabel", { number: activeChapter.chapterNumber })}
              </div>
              <h1
                style={{
                  margin: "0 0 32px",
                  fontFamily,
                  fontSize: "1.9rem",
                  fontWeight: 500,
                  color: accent,
                  letterSpacing: "0.01em",
                }}
              >
                {activeChapter.title}
              </h1>
              {renderMarkdownBody(activeChapter.bodyMd, fontFamily)}
              {showBranchActions && onForkToStory && (
                <div style={branchWrap}>
                  <p style={branchHint}>
                    {t("bookshelf:bookReader.continueHereHint", {
                      defaultValue:
                        "Branch a new, private continuation that picks up from the end of this chapter. Nothing is published.",
                    })}
                  </p>
                  <button
                    type="button"
                    style={branchBtn(
                      forkingChapter === activeChapter.chapterNumber,
                    )}
                    disabled={forkingChapter !== null}
                    onClick={() => {
                      const ok =
                        typeof window === "undefined" ||
                        window.confirm(
                          t("bookshelf:bookReader.continueHereConfirm", {
                            number: activeChapter.chapterNumber,
                            defaultValue: `Branch a new private continuation from the end of Chapter ${activeChapter.chapterNumber}?`,
                          }),
                        );
                      if (ok) void handleForkFromChapter(activeChapter.chapterNumber);
                    }}
                  >
                    {forkingChapter === activeChapter.chapterNumber
                      ? t("bookshelf:bookReader.continueHereBusy", {
                          defaultValue: "Branching…",
                        })
                      : t("bookshelf:bookReader.continueHere", {
                          defaultValue: "Continue the story from here",
                        })}
                  </button>
                </div>
              )}
            </>
          ) : (
            !loading && (
              <div style={empty}>{t("bookshelf:bookReader.selectChapter")}</div>
            )
          )}
        </main>
      </div>

      <footer style={footer}>
        <button type="button" style={footerLeftBtn} onClick={onContinuePlaying}>
          {t("bookshelf:bookReader.continuePlaying")}
        </button>
        <button
          type="button"
          style={shareBtn}
          onClick={() => setShareOpen(true)}
          aria-label={t("bookshelf:bookReader.shareAria")}
        >
          {t("bookshelf:bookReader.share")}
        </button>
      </footer>

      {shareOpen && (
        <ShareModal
          storyId={storyId}
          storyName={storyName}
          authorName={authorName}
          scenarioName={t(`scenarios:${scenarioId}.pillLabel`, {
            defaultValue: presentation?.pillLabel ?? scenarioId,
          })}
          accent={accent}
          accentMuted={accentMuted}
          ink={ink}
          inkMuted={inkMuted}
          paper={paper}
          paperEdge={paperEdge}
          fontFamily={fontFamily}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
